import { api } from '@/lib/api-client'
import type {
  AdminRefund,
  PageResponse,
  RefundAdminOption,
  RefundCreateRequest,
  RefundEligibility,
  RefundFilters,
  RefundSummary,
} from '@/types/api'

/**
 * The refund API.
 *
 * Note what is absent: there is no delete and no update. Refund rows are
 * financial audit records, and the backend exposes no endpoint that would
 * remove or rewrite one.
 */

export function fetchRefunds(filters: RefundFilters): Promise<PageResponse<AdminRefund>> {
  return api.get<PageResponse<AdminRefund>>('/api/admin/refunds', { query: { ...filters } })
}

/** Totals over the same filters as the table, computed server-side so the two agree. */
export function fetchRefundSummary(filters: RefundFilters): Promise<RefundSummary> {
  const { page: _page, size: _size, sort: _sort, direction: _direction, ...rest } = filters
  return api.get<RefundSummary>('/api/admin/refunds/summary', { query: { ...rest } })
}

export function fetchRefund(id: number): Promise<AdminRefund> {
  return api.get<AdminRefund>(`/api/admin/refunds/${id}`)
}

/** Every attempt against the same original transaction, newest first. */
export function fetchRefundHistory(id: number): Promise<AdminRefund[]> {
  return api.get<AdminRefund[]>(`/api/admin/refunds/${id}/history`)
}

export function fetchRefundAdmins(): Promise<RefundAdminOption[]> {
  return api.get<RefundAdminOption[]>('/api/admin/refunds/admins')
}

/**
 * Whether a booking can be refunded and for how much.
 *
 * This is the only thing that decides if the Refund action appears. The
 * dashboard does not guess from a payment-status string, because "PAID" alone
 * says nothing about whether a Paymob transaction id exists or how much of the
 * charge has already come back.
 */
export function fetchRefundEligibility(tripRequestId: number): Promise<RefundEligibility> {
  return api.get<RefundEligibility>(`/api/admin/refunds/eligibility/trip-request/${tripRequestId}`)
}

/**
 * Issues a refund.
 *
 * The body carries no transaction id, no original amount and no administrator
 * identity — all three are resolved server-side from the booking and the JWT.
 * The idempotency key makes a repeated submission return the first result
 * rather than sending a second refund.
 */
export function createRefund(request: RefundCreateRequest): Promise<AdminRefund> {
  return api.post<AdminRefund>('/api/admin/refunds', request)
}

/** Asks Paymob what actually happened to an uncertain refund. Never re-sends it. */
export function reconcileRefund(id: number): Promise<AdminRefund> {
  return api.post<AdminRefund>(`/api/admin/refunds/${id}/reconcile`)
}
